import {
    Ion,
    Cartesian3,
    Quaternion,
    Matrix3,
    Transforms,
    Math as cMath,
    JulianDate,
    SampledPositionProperty,
    Color,
    IonResource,
    NearFarScalar,
    Cartesian2
} from "cesium";

// import { initStore, getStore, clearAllStores } from './entityStore';

Ion.defaultAccessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI3MzNkMzdmZC04NDYwLTQ2M2MtYWU0Ny1mZDFhMTg0ZWU3MWYiLCJpZCI6MTY1MzM3LCJpYXQiOjE2OTQxNzgzNzd9.UdrLgwjyqX2wVn0otuuJSjwl7gkYLvkvFkpFJ191Ot8';

// 3D Model IDs
const modelId = '3369804'; // 3D North Star
// const modelId = '3369903'; // 3D Star
// const modelId = '2500563'; // Grey 3D Arrow
// const modelId = '1'; // Invalid model (disables models)

// White 3D Arrow: 
// const modelId = '2428076';
  
const DEFAULT_PLANE_MODEL = modelId;
const SUPPORTED_MODELS = [modelId];
const MODEL_URLS = {};
SUPPORTED_MODELS.forEach((modelId) => {
    IonResource.fromAssetId(modelId).then((modelURL) => MODEL_URLS[modelId] = modelURL);
})
  
// Enable Points by default, unless explicitly set to false
// Disable other display options by default, unless explicitly set to true
const SHOW_POINTS = localStorage.getItem('SHOW_POINTS') === 'false' ? false : true;
const SHOW_ICONS = localStorage.getItem('SHOW_ICONS') === 'true';
const SHOW_BOXES = localStorage.getItem('SHOW_BOXES') === 'true';
const SHOW_LINES = localStorage.getItem('SHOW_LINES') === 'true';
const SHOW_MODEL_FOR_ALL_POINTS = localStorage.getItem('SHOW_MODEL_FOR_ALL_POINTS') === 'true';
const SHOW_MODEL_FOR_LAST_POINT = localStorage.getItem('SHOW_MODEL_FOR_LAST_POINT') === 'true';
  
const DATA_POINT_OPTIONS = {
    ADSB: {
        color: Color.fromAlpha(Color.RED, 1),
        pixelSize: 8,
        // image: 'icons/redStar.png',
    },
    Chronos: {
        color: Color.LIME,
        // outlineWidth: 0,
        // outlineColor: Color.LIME,
        pixelSize: 12,
        // image: 'icons/limeStar.png',
        image: 'icons/limeCircleXSmall.png',
    },
    FlightAware: {
        color: Color.fromAlpha(Color.CHARTREUSE, 1),
        pixelSize: 8,
    },
    RID: {
        color: Color.fromAlpha(Color.PLUM, 1),
        pixelSize: 8,
        // image: 'icons/yellowStar.png',
    },
    ValidADSB: {
        color: Color.fromAlpha(Color.ORANGE, 1),
        pixelSize: 8,
        // image: 'icons/yellowStar.png',
    },
    Testing: {
        color: Color.fromAlpha(Color.YELLOW, 1),
        pixelSize: 8,
        // image: 'icons/yellowStar.png',
    },
    default: {
        color: Color.GREEN,
        pixelSize: 8,
    }
};

const getTrackId = (point, namespace) => {
    return namespace ? `${namespace}-${point.id}` : point.id;
};

const positionsAreEqual = (pos1, pos2) => {
    if (pos1.x === pos2.x && pos1.y === pos2.y && pos1.z === pos2.z) {
        return true;
    }
    return false;
};

const getTypeDisplayOptions = (dataPointType) => {
    return DATA_POINT_OPTIONS[dataPointType] ?? DATA_POINT_OPTIONS.default;
};

const metersToFeet = (valInMeters) => {
    if (valInMeters == null) {
        return '';
    }

    return Math.round(valInMeters * 3.28084);
};

const getPointDescription = (dataPoint) => {
    const isoTime = (new Date(dataPoint.time)).toISOString();
    return `${isoTime ? `Time: ${isoTime}, ` : ''},
        ID: ${dataPoint.id},
        Lat: ${dataPoint.latitude}, 
        Lon: ${dataPoint.longitude}, 
        Alt: ${metersToFeet(dataPoint.height)} ft`;
};

const getPointEntitiy = (
    id,
    dataPointIndx,
    position,
    dataPoint,
) => {
    const typeDisplayOptions = getTypeDisplayOptions(dataPoint.type);

    return {
        id: `${id}-point-${dataPointIndx}`,
        description: getPointDescription(dataPoint),
        position: position,
        point: {
            ...typeDisplayOptions,
            ...(dataPoint.displayOptions?.pointOptions || {}),
            color: dataPoint.displayOptions?.color || typeDisplayOptions.color,
        },
        // label: dataPointIndx === track.length - 1 ? {		// Only add label to the last point in each track
        //     text: dataPoint.id,
        //     verticalOrigin: VerticalOrigin.BOTTOM,
        //     horizontalOrigin: HorizontalOrigin.RIGHT,
        //     eyeOffset: pointOptions.eyeOffset,
        // } : {},
        label: {},
    };

};

const getBoxEntity = (
    trackId,
    dataPointIndx,
    position,
    dataPoint
) => {
    const typeDisplayOptions = getTypeDisplayOptions(dataPoint.type);
    const pointColor = dataPoint.displayOptions?.color || typeDisplayOptions.color;
    
    return {
        id: `${trackId}-box-${dataPointIndx}`,
        description: getPointDescription(dataPoint),
        position: position,
        box: {
            dimensions: new Cartesian3(10000, 10000, 10000),
            material: new Color(
                pointColor.red,
                pointColor.green,
                pointColor.blue,
                pointColor.alpha
            ),
            outline: true,
            outlineColor: Color.BLACK,
            fill: true,
            show: true
        },
    };
};

const getIconEntity = (
    trackId,
    dataPointIndx,
    position,
    dataPoint
) => {
    const typeDisplayOptions = getTypeDisplayOptions(dataPoint.type);
    const billboard = {
        image: dataPoint.displayOptions?.image || typeDisplayOptions.image,
        pixelOffset: new Cartesian2(0, 0),
        eyeOffset: new Cartesian3(0, 0, -3000),
        horizontalOrigin: 0,
        verticalOrigin: 0,
        scale: 1,
        scaleByDistance: new NearFarScalar(0.0, 1.0, 1000.0, 0.01),
    };
    return {
        id: `${trackId}-billboard-${dataPointIndx}`,
        description: getPointDescription(dataPoint),
        position: position,
        billboard: {
            ...billboard,
            ...(dataPoint.displayOptions?.billboardOptions || {}),
        },
    };
};

const getLineEntity = (
    trackId,
    firstPosition,
    lastPosition,
    lastDataPoint
) => {
    const line = {
        polyline: {
            positions: [firstPosition, lastPosition],
            width: 3,
            material: Color.YELLOW,
        }
    };
    const pointOptions = getTypeDisplayOptions(lastDataPoint.type);
    
    return {
        id: `${trackId}-line`,
        description: getPointDescription(lastDataPoint),
        position: lastPosition,
        point: pointOptions,
        ...line
    };
};

const getSinglePointModelEntity = (
    trackId,
    position,
    modelId = DEFAULT_PLANE_MODEL,
    dataPoint
) => {
    try {
        // console.log('getSinglePointModelEntity', trackId, position, modelId, dataPoint);
        if (!SUPPORTED_MODELS.includes(modelId)) {
            modelId = DEFAULT_PLANE_MODEL;
        }

        const modelUri = MODEL_URLS[modelId];

        const modelEntity = {
            name: modelUri,
            id: trackId,
            position: position,
            model: {
                scale: 1000,
                uri: modelUri,
                minimumPixelSize: 20,
            },
            description: dataPoint ? getPointDescription(dataPoint) : trackId,
        };

        return modelEntity;
    } catch (e) {
        console.error('getSinglePointModelEntity error', e);
        return null;
    }    
}

const getTwoPointsModelEntity = (
    trackId,
    position,
    prevPosition,
    modelId = DEFAULT_PLANE_MODEL,
    dataPoint,
) =>{
    try {
        // console.log('getTwoPointsModelEntity', trackId, position, prevPosition, modelId, dataPoint);
        if (!SUPPORTED_MODELS.includes(modelId)) {
            modelId = DEFAULT_PLANE_MODEL;
        }

        const modelUri = MODEL_URLS[modelId];

        // if (viewer.entities.getById(trackId)) {
        //     viewer.entities.removeById(trackId);
        // }

        // console.log('getTwoPointsModelEntity: position', position, 'prevPosition', prevPosition, 'modelId', modelId, 'dataPoint', dataPoint);
        var direction = Cartesian3.subtract(position, prevPosition, new Cartesian3());
        Cartesian3.normalize(direction, direction);
        
        var rotationMatrix = Transforms.rotationMatrixFromPositionVelocity(position, direction);
        
        // var rot90 = Matrix3.fromRotationY(cMath.toRadians(90));
        // Matrix3.multiply(rotationMatrix, rot90, rotationMatrix);
        
        // flip model on x axis
        // rotationMatrix = Matrix3.multiply(rotationMatrix, Matrix3.fromRotationX(cMath.PI), new Matrix3());
        
        // flip model on z axis
        // rotationMatrix = Matrix3.multiply(rotationMatrix, Matrix3.fromRotationY(- 1 * cMath.PI / 2), new Matrix3());
        // rotationMatrix = Matrix3.multiply(rotationMatrix, Matrix3.fromRotationX(cMath.PI), new Matrix3());
        rotationMatrix = Matrix3.multiply(rotationMatrix, Matrix3.fromRotationZ(cMath.PI), new Matrix3());

        const heading = Quaternion.fromRotationMatrix(
            rotationMatrix
        )

        const modelEntity = {
            name: modelUri,
            id: trackId,
            position: position,
            model: {
                scale: 1,
                uri: modelUri,
                minimumPixelSize: 20,
            },
            orientation: heading,
            description: dataPoint ? getPointDescription(dataPoint) : trackId,
        };

        // const store = getStore(modelId);
        // store.models.push(modelEntity);

        return modelEntity;
    } catch (e) {
        console.error('getTwoPointsModelEntity error', e);
        return null;
    }    
}

const handleTimeRange = (time, startTime, stopTime, mainStart, mainStop) => {
    let newStartTime = startTime;
    let newStopTime = stopTime;
    let newMainStart = mainStart;
    let newMainStop = mainStop;
    
    if (!startTime || JulianDate.lessThan(time, startTime)){
        newStartTime = time.clone();
    }
    if (!stopTime || JulianDate.greaterThan(time, stopTime)){
        newStopTime = time.clone();
    }
    if (!mainStart || JulianDate.lessThan(time, mainStart)){
        newMainStart = time.clone();
    }
    if (!mainStop || JulianDate.greaterThan(time, mainStop)){
        newMainStop = time.clone();
    }

    return { newStartTime, newStopTime, newMainStart, newMainStop };
};
  
export const loadTrack = (track, live, namespace = null) => {
    let trackId;
    const entities = [];

    let mainStart;
    let mainStop;
    let startTime;
    let stopTime;

    // Stores all timed positions of the plane in the track, used for animating flight
    const positionProperty = new SampledPositionProperty();

    // Store last two positions of the plane in the track, used to calculate model heading in live tracking
    let lastDataPoint;
    let lastPosition;
    let prevPosition;

    for(let dataPointIndx = 0; dataPointIndx < track.length; dataPointIndx++) {
        const dataPoint = track[dataPointIndx];

        // Do not load points without track id
        if (!dataPoint.id) {
            continue;
        }

        if (!trackId) {
            trackId = getTrackId(dataPoint, namespace);
        }
        
        const isoTime = (new Date(dataPoint.time)).toISOString();
        const time = JulianDate.fromIso8601(isoTime, new JulianDate()); 
        const position = Cartesian3.fromDegrees(dataPoint.longitude, dataPoint.latitude, dataPoint.height);
        
        if (!live) {
            positionProperty.addSample(time, position);
        }

        if (live && dataPointIndx === track.length - 1) {
            lastDataPoint = dataPoint;
            lastPosition = position;
        }

        if (live && dataPointIndx === track.length - 2) {
            let indexForPrevPos = dataPointIndx;

            // Find the previous position that is not equal to the last position
            // This is to avoid errors when calculating the heading if the last two points are the same
            while (indexForPrevPos >= 0) {
                const prevDataPoint = track[indexForPrevPos];
                const tempPrevPosition = Cartesian3.fromDegrees(prevDataPoint.longitude, prevDataPoint.latitude, prevDataPoint.height);
                if (!positionsAreEqual(tempPrevPosition, position)) {
                    prevPosition = tempPrevPosition;
                    break;
                }
                indexForPrevPos--;
            }
        }

        const { newStartTime, newStopTime, newMainStart, newMainStop } = handleTimeRange(time, startTime, stopTime, mainStart, mainStop);
        startTime = newStartTime;
        stopTime = newStopTime;
        mainStart = newMainStart;
        mainStop = newMainStop;

        if (
            dataPoint.displayOptions?.showPoint ||
            (SHOW_POINTS && dataPoint.displayOptions?.showPoint !== false)
        ) {
            const pointEntity = getPointEntitiy(
                trackId,
                dataPointIndx,
                position,
                dataPoint,
            );
            entities.push(pointEntity);
        }
        
        if (
            dataPoint.displayOptions?.showBox ||
            (SHOW_BOXES && dataPoint.displayOptions?.showBox !== false)
        ) {
            const rectangleEntity = getBoxEntity(
                trackId,
                dataPointIndx,
                position,
                dataPoint,
            );
            entities.push(rectangleEntity);
        }
        
        if (
            dataPoint.displayOptions?.showIcon ||
            (SHOW_ICONS && dataPoint.displayOptions?.showIcon !== false)
        ) {
            const rectangleEntity = getIconEntity(
                trackId,
                dataPointIndx,
                position,
                dataPoint,
            );
            entities.push(rectangleEntity);
        }
        
        if (
            !live ||
            dataPoint.displayOptions?.showModel ||
            (SHOW_MODEL_FOR_ALL_POINTS && dataPoint.displayOptions?.showModel !== false)
        ) {
            entities.push({
                ...getSinglePointModelEntity(
                    trackId,
                    position,
                    DEFAULT_PLANE_MODEL,
                    dataPoint,
                ),
                id: `${trackId}-${dataPointIndx}-model`,
            });

            // const store = getStore(id);
            // store.points.push(addedEntity);
        }
    }

    if (SHOW_LINES && track.length > 1) {
        const firstPosition = Cartesian3.fromDegrees(track[0].longitude, track[0].latitude, track[0].height);
        const lineEntity = getLineEntity(
            trackId,
            firstPosition,
            lastPosition,
            lastDataPoint
        );
        entities.push(lineEntity);
    }
    
    let modelEntity
    if (SHOW_MODEL_FOR_LAST_POINT && live && trackId && lastPosition && prevPosition && lastDataPoint) {
        modelEntity = getTwoPointsModelEntity(
            trackId,
            lastPosition,
            prevPosition,
            DEFAULT_PLANE_MODEL,
            lastDataPoint,
        );
    }

    return { entities, modelEntity, mainStart, mainStop, startTime, stopTime };
}
  
// const flyCameraTo = (point, height = 120000) => {
//     viewer.camera.flyTo({
//         destination: Cartesian3.fromDegrees(point.longitude, point.latitude, height)
//     });
// }
